import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useIdentidade } from "@/lib/identidade";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import logoEntrada from "@/assets/logo-dark-t.png";

export default function Login() {
  const { login, user } = useAuth();
  const ident = useIdentidade();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const [next] = useState(() => new URLSearchParams(window.location.search).get("next"));
  const destino = (u) => (u?.must_change_password ? "/primeiro-acesso" : next && next.startsWith("/") && !next.startsWith("//") ? next : (u?.platform_role === "superadmin" ? "/master" : "/dashboard"));
  useEffect(() => {
    if (user) navigate(destino(user), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Bem-vindo de volta!");
      navigate(destino(u), { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.response?.data?.error;
      const msg = !err.response ? "Servidor indisponível no momento. Se você está usando o link de pré-visualização (preview), ele hiberna quando fica inativo — aguarde 30s e tente de novo, ou use o link publicado (Deploy)."
        : status === 429 ? formatApiError(detail) || "Muitas tentativas. Aguarde 1 minuto."
        : formatApiError(detail) || "Falha no login";
      toast.error(msg, { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-8 relative overflow-hidden" style={{ background: "var(--fundo-marca, #000617)" }} data-testid="login-page">
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="w-full max-w-sm fade-up flex flex-col items-center relative">
        <img src={ident.src("imagem_entrada", logoEntrada)} alt="TRT GESTÃO — Gerenciamento de Contratos" className="h-56 sm:h-72 w-auto max-w-full object-contain" data-testid="login-brand-img" />

        <form onSubmit={submit} className="w-full mt-2 space-y-4">
          <div>
            <Label htmlFor="email" className="text-slate-200">Email</Label>
            <Input
              id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com" data-testid="login-email-input" className="mt-1.5 h-11 bg-white text-slate-900 border-white/20"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-slate-200">Senha</Label>
            <div className="relative mt-1.5">
              <Input
                id="password" type={show ? "text" : "password"} required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                data-testid="login-password-input" className="h-11 bg-white text-slate-900 border-white/20 pr-10"
              />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" aria-label="Mostrar senha">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit-button"
            className="w-full bg-gold hover:bg-[hsl(var(--gold-hover))] text-white h-11 font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
          </Button>
        </form>
        <Link to="/esqueci-senha" className="block text-center text-sm text-slate-200 hover:underline mt-4" data-testid="forgot-link">Esqueci minha senha</Link>
        <div className="flex items-center gap-2 text-slate-400 text-xs mt-8"><ShieldCheck className="w-4 h-4 gold-text" /> Dados protegidos • Acesso por permissões • LGPD</div>
        <p className="text-[11px] text-slate-500 mt-3 text-center">© 2026 TRT GESTÃO — Gerenciamento de Contratos. Uso interno restrito.</p>
      </div>
    </div>
  );
}
